import { useUpdateAppointment } from '../../../context/Appointment/appointment';
import { useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { GMAppointmentName } from '../../../controls/AppointmentName';
import { CTAButton } from '../../../controls/ButtonBase';
import { Heading } from '../../../controls/Heading/Heading';
import { ScrollableBody } from '../../Layout/ScrollableBody/ScrollableBody';
import { GMButtonGroup } from '../../../controls/ButtonGroup';
import { GMRoundedIcon, GMSizedIcon } from '../../../controls/Icon';
import { GoMotoComponent } from '../../../API/GoMoto.types';
import { useRestartOnBackAfterSignature } from '../../../helpers/Hooks';
import { SurveyStep1Props } from './SurveyStep1.types';
import { memo, useEffect } from 'react';

/**
 * Survey component that asks the user a Yes/No question and gives them the respective buttons
 * to answer with. When they click either Yes or No, the screen proceeds and updates the appointment
 * with their response.
 *
 * This screen skips if the dealer does not have `surveyEnabled` set to true.
 *
 * @param onSkip - {@link SurveyStep1Props.onSkip onSkip} description.
 * @param onProceed - {@link SurveyStep1Props.onProceed onProceed} description.
 */
export const SurveyStep1: GoMotoComponent<SurveyStep1Props> = memo(({
    onSkip,
    onProceed,
}) => {
    useRestartOnBackAfterSignature();
    const {
        dealer: { surveyEnabled },
    } = useKioskState();
    const Localization = useLocalization();
    const { updateSurveyQuestions } = useUpdateAppointment();

    useEffect(() => {
        if (!surveyEnabled) {
            onSkip();
        }
    }, [onSkip, surveyEnabled]);

    if (!surveyEnabled) {
        return null;
    }

    return (
        <ScrollableBody
            heading={
                <Heading
                    roundedIcon={
                        <GMRoundedIcon
                            roundedBackgroundSize={'large'}
                            themeType={'secondary'}
                        >
                            <GMSizedIcon
                                iconType={'CSI'}
                                iconSize={'extraLarge'}
                                ariaLabel={Localization.HandshakeIcon}
                            />
                        </GMRoundedIcon>
                    }
                    primaryHeading={Localization.formatString(Localization.CustomerBeforeWeWrapUp, <GMAppointmentName />) as string}
                    secondaryHeading={Localization.DidWeAnswerAllYourQuestions}
                    showMustache
                />
            }
            proceedButtons={
                <GMButtonGroup
                    alignment={'center'}
                    buttons={[
                        (
                            <CTAButton
                                type={'button'}
                                styleType={'primary'}
                                onClick={() => {
                                    updateSurveyQuestions({ customerWouldRepeat: true });
                                    onProceed();
                                }}
                                title={Localization.ClickToConfirmQuestionsAnswered}
                                label={Localization.YesYouDid}
                                kioskOptions={{ square: true }}
                                id={Localization.ClickToConfirmQuestionsAnswered}
                                ariaLabel={Localization.ClickToConfirmQuestionsAnswered}
                            />
                        ),
                        (
                            <CTAButton
                                type={'button'}
                                styleType={'secondary'}
                                onClick={() => {
                                    updateSurveyQuestions({ customerWouldRepeat: false });
                                    onProceed();
                                }}
                                title={Localization.ClickToConfirmQuestionsNotAnswered}
                                label={Localization.NoYouDidNot}
                                kioskOptions={{
                                    square: true,
                                    squareOptions: {
                                        roundedIcon: (
                                            <GMRoundedIcon
                                                themeType={'primary'}
                                                allowInvertedPrimary
                                                roundedBackgroundSize={'large'}
                                            >
                                                <GMSizedIcon iconSize={'custom'} iconType={'No'} />
                                            </GMRoundedIcon>
                                        ),
                                    },
                                }}
                                id={Localization.ClickToConfirmQuestionsNotAnswered}
                                ariaLabel={Localization.ClickToConfirmQuestionsNotAnswered}
                            />
                        ),
                    ]}
                />
            }
            unsetButtons
        />
    );
});
